import { PHYSICS_EPSILON } from '@md/shared';
import type { TileDef } from '../level/registry.js';
import { type Aabb, aabbOverlaps, aabbTouches, tileAabb } from './aabb.js';
import type { Body } from './body.js';

/**
 * Détection des tuiles au CONTACT d'un corps.
 *
 * Une tuile solide ne peut jamais être recouverte par un corps : la résolution
 * de collision l'arrête exactement sur sa face. Toute règle du type « la dalle
 * sous mes pieds est électrifiée » ou « le plafond que je touche est en acier »
 * a donc besoin d'un test de CONTACT, pas de recouvrement — c'est ce que
 * fournit ce module, de façon totalement générique (le moteur ignore ce que
 * les tuiles signifient).
 */

/** Position d'une tuile par rapport au corps qui la touche. */
export type ContactSide = 'above' | 'below' | 'left' | 'right' | 'inside';

export interface TileContact {
  readonly tile: TileDef;
  readonly tx: number;
  readonly ty: number;
  readonly side: ContactSide;
}

/** Vue minimale d'un niveau pour la détection de contacts. */
export interface TileSource {
  tileAt(tx: number, ty: number): TileDef;
}

function sideOf(box: Aabb, tx: number, ty: number): ContactSide {
  if (Math.abs(ty + 1 - box.y) <= PHYSICS_EPSILON) {
    return 'above';
  }
  if (Math.abs(ty - (box.y + box.h)) <= PHYSICS_EPSILON) {
    return 'below';
  }
  if (Math.abs(tx + 1 - box.x) <= PHYSICS_EPSILON) {
    return 'left';
  }
  if (Math.abs(tx - (box.x + box.w)) <= PHYSICS_EPSILON) {
    return 'right';
  }
  return 'inside';
}

/**
 * Tuiles touchées ou recouvertes par le corps.
 *
 * Les tuiles SOLIDES comptent dès qu'elles partagent une face avec le corps ;
 * les tuiles traversables (pics, zone dégaussante…) exigent un recouvrement
 * réel, sinon un robot mourrait en frôlant une case de pics voisine.
 */
export function tileContacts(body: Body, level: TileSource): TileContact[] {
  const box: Aabb = { x: body.x, y: body.y, w: body.w, h: body.h };
  const contacts: TileContact[] = [];
  const tx0 = Math.floor(body.x - PHYSICS_EPSILON);
  const tx1 = Math.floor(body.x + body.w + PHYSICS_EPSILON);
  const ty0 = Math.floor(body.y - PHYSICS_EPSILON);
  const ty1 = Math.floor(body.y + body.h + PHYSICS_EPSILON);
  for (let ty = ty0; ty <= ty1; ty += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const tile = level.tileAt(tx, ty);
      const cell = tileAabb(tx, ty);
      const touching = tile.solid ? aabbTouches(box, cell) : aabbOverlaps(box, cell);
      if (touching) {
        contacts.push({ tile, tx, ty, side: sideOf(box, tx, ty) });
      }
    }
  }
  return contacts;
}

/** Le corps touche-t-il au moins une tuile dont le caractère est listé ? */
export function touchesTileChar(
  body: Body,
  level: TileSource,
  chars: readonly string[],
): TileContact | null {
  if (chars.length === 0) {
    return null;
  }
  for (const contact of tileContacts(body, level)) {
    if (chars.includes(contact.tile.char)) {
      return contact;
    }
  }
  return null;
}
